import { lazySubscribe, NotificationSubscription } from 'owa-notification';
import type { Dispatch } from 'redux';
import { createThunk, PersonType, uniq, ThunkDispatch } from '../../../shared';
import { getPackageState, getAppState, invalidateCachedContacts } from '../../../appstate';
import debounce from 'lodash-es/debounce';
import { getDistributionListAndNotes } from './distributionListDataActions';
import { refreshCurrentPersonaListData } from '../user/personaListActions';
import { getCurrentHubPersona } from '../../selectors/currentPersonaSelector';
import { getPublicFolderForCurrentHubPersona } from '../../selectors/publicFolderSelector';
import { packageStateKey, HubState } from '../../state';
import { FocusedViewFilter, RowNotificationPayload } from '../../../service';

// This mapping is to ensure we don't double-sub to folders, not worth putting this in the state tree
const currentSubscriptions: { [folderId: string]: NotificationSubscription } = {};

export function subscribeToFolderNotifications(folderId: string, dispatch: ThunkDispatch): void {
    if (currentSubscriptions[folderId]) {
        return;
    }

    const subscription: NotificationSubscription = {
        subscriptionId: folderId,
        requiresExplicitSubscribe: true,
        subscriptionParameters: {
            NotificationType: 'RowNotification',
            FolderId: folderId,
            FocusedViewFilter: FocusedViewFilter.None,
        },
    };
    currentSubscriptions[folderId] = subscription;
    lazySubscribe.importAndExecute(subscription, (payload: RowNotificationPayload) => {
        dispatch(onNotification(payload, folderId));
    });
}

/**
 * Handles a notification.
 * We only depend on notifications for user actions that originate outside of the current session (different client/tab).
 * However, as we currently have no good way of determining the origin of a user action, we will also get notifications
 * for user actions (link,unlink,create,edit etc) performed within this session.
 */
const onNotification = (notification: RowNotificationPayload, folderId: string) =>
    createThunk('OnNotificationThunk', (dispatch: ThunkDispatch, getState: () => {}) => {
        const appState = getAppState(getState());
        const isInSearch = !!appState.view.navigationState.currentQuery;
        const contactFoldersState = appState.data.contactFoldersState;
        if (isInSearch || !contactFoldersState.shouldProcessUpdateNotifications) {
            // Notifications disabled. We do this for certain operations such as import/export.
            // Do nothing while notifications is disabled.
            return;
        }

        // We only care about added, deleted and modified
        const eventType = notification.EventType;
        if (eventType === 'RowAdded' || eventType === 'RowDeleted' || eventType === 'RowModified') {
            // We only care about modified contacts for cache invalidation.
            // New contacts will be fetched from mailbox on demand and removed contacts will eventually fall off the client
            // cache during reload (LPC) or when TTL is reached (LOKI).
            // Also, LPC/Loki does not deal with private distribution lists.
            if (eventType === 'RowModified' && notification.Item.ItemClass === 'IPM.Contact') {
                // For notifications, the conversationId property holds the personaId.
                personaIdsToInvalidateInLPCAndLOKI.push(notification.Item.ConversationId.Id);
            }

            // Invoke the debounced function that will do the actual work
            invalidateCacheAndRefreshData(dispatch);
        }
    });

let personaIdsToInvalidateInLPCAndLOKI: string[] = [];

// Wait time before processing a notification
const notificationDebounceWaitTime = 5000; // 5 seconds

/**
 * This function invalidates the LPC/LOKI cache entries for all personas we have gotten a notification for.
 * Additionally, it refreshes the list of personas we are currently displaying in the persona list and also gets fresh
 * data for the currently displayed Person(EV) or PrivateDL.
 * We don't need to get fresh data for all folders where notifications have triggered because we will get fresh folder data whenever
 * the user is navigating to this folder.
 * NOTE: Certain user actions may trigger a lot of notifications, even for the same personaId (e.g when linking). We therefore
 * do debouncing in order to avoid doing unnecessary work and hitting the throttling limit on the mailbox.
 * The debounce wait time is set relatively high as we only depend on notifications for actions that originate outside the current session.
 */
const invalidateCacheAndRefreshData = debounce((dispatch: ThunkDispatch) => {
    // Invalidate LPC/LOKI cache and refresh data for currently displayed EV persona
    if (personaIdsToInvalidateInLPCAndLOKI.length > 0) {
        invalidateCachedContacts(dispatch)(uniq(personaIdsToInvalidateInLPCAndLOKI));
        personaIdsToInvalidateInLPCAndLOKI = [];
    }

    // Refresh persona list (goes directly to OWS)
    dispatch(refreshCurrentPersonaListData);

    // Get fresh data for the currently displayed PDL
    dispatch(refreshCurrentPrivateDL);
}, notificationDebounceWaitTime);

/**
 * Gets fresh data for the currently displayed PDL.
 */
const refreshCurrentPrivateDL = createThunk(
    'RefreshCurrentPrivateDL',
    (dispatch: Dispatch, getState: () => {}): void => {
        const state = getPackageState<HubState>(getState(), packageStateKey);
        const currentPersona = getCurrentHubPersona(state.appState, state.packageState);
        const publicFolderId = getPublicFolderForCurrentHubPersona(
            state.appState,
            state.packageState
        );
        if (currentPersona && currentPersona.type === PersonType.PrivateDistributionList) {
            getDistributionListAndNotes(dispatch)(
                currentPersona.personaId,
                currentPersona.hubPersonaId,
                publicFolderId?.id,
                {
                    type: PersonType.PrivateDistributionList,
                    itemId: currentPersona.itemId,
                }
            );
        }
    }
);
